import { useState, useRef, useEffect } from 'react';

const BOOKED_DATES: string[] = [
  '2025-08-02', '2025-08-03', '2025-08-09', '2025-08-16',
  '2025-08-17', '2025-08-23', '2025-08-30',
  '2025-09-06', '2025-09-07', '2025-09-13', '2025-09-20',
  '2025-09-21', '2025-09-27',
  '2025-10-04', '2025-10-05', '2025-10-11', '2025-10-18',
  '2025-10-19', '2025-10-25', '2025-10-26',
  '2025-11-01', '2025-11-08', '2025-11-15', '2025-11-22',
  '2025-11-29', '2025-11-30',
  '2025-12-06', '2025-12-13', '2025-12-14', '2025-12-20',
  '2025-12-21', '2025-12-24', '2025-12-25', '2025-12-26',
  '2025-12-27', '2025-12-31',
];

const LIMITED_DATES: string[] = [
  '2025-08-10', '2025-08-24',
  '2025-09-14', '2025-09-28',
  '2025-10-12', '2025-10-26',
  '2025-11-09', '2025-11-23',
  '2025-12-07',
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function toDateStr(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

export default function AvailabilityCalendar() {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const goToPrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
    setSelectedDate(null);
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
    setSelectedDate(null);
  };

  const isPast = (year: number, month: number, day: number): boolean => {
    const d = new Date(year, month, day);
    const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return d < t;
  };

  const canGoPrev = (): boolean => {
    return (
      currentYear > today.getFullYear() ||
      (currentYear === today.getFullYear() && currentMonth > today.getMonth())
    );
  };

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);

  const getDateStatus = (dateStr: string): 'booked' | 'limited' | 'available' | 'past' => {
    const [y, m, d] = dateStr.split('-').map(Number);
    if (isPast(y, m - 1, d)) return 'past';
    if (BOOKED_DATES.includes(dateStr)) return 'booked';
    if (LIMITED_DATES.includes(dateStr)) return 'limited';
    return 'available';
  };

  const handleDayClick = (dateStr: string, status: string) => {
    if (status === 'past' || status === 'booked') return;
    setSelectedDate((prev) => (prev === dateStr ? null : dateStr));
  };

  const selectedStatus = selectedDate ? getDateStatus(selectedDate) : null;

  const scrollToQuote = () => {
    const el = document.getElementById('quote-estimator-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const cells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  // Pad to complete last row
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <section
      ref={ref}
      className={`py-24 px-6 lg:px-12 bg-white transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-amber-600 text-xs tracking-[0.35em] uppercase font-medium mb-2">
            Check Before You Book
          </p>
          <h2
            className="text-4xl font-light text-stone-800 mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Live Availability Calendar
          </h2>
          <div className="w-14 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent mx-auto mb-5" />
          <p className="text-stone-500 text-sm max-w-xl mx-auto leading-relaxed">
            Check which dates are open before submitting your quote request. Select an available date to see its status and proceed with your enquiry.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          {/* Calendar */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-stone-100 shadow-lg shadow-stone-100 overflow-hidden">
              {/* Month Navigation */}
              <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-br from-stone-800 to-stone-900">
                <button
                  type="button"
                  onClick={goToPrevMonth}
                  disabled={!canGoPrev()}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-left-s-line text-lg"></i>
                </button>
                <div className="text-center">
                  <p className="text-white font-semibold text-base tracking-wide">
                    {MONTH_NAMES[currentMonth]} {currentYear}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={goToNextMonth}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </button>
              </div>

              {/* Day Headers */}
              <div className="grid grid-cols-7 border-b border-stone-100">
                {DAY_NAMES.map((day) => (
                  <div
                    key={day}
                    className="py-3 text-center text-xs font-semibold text-stone-400 uppercase tracking-wider"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 p-3 gap-1">
                {cells.map((day, idx) => {
                  if (day === null) {
                    return <div key={`empty-${idx}`} className="aspect-square" />;
                  }
                  const dateStr = toDateStr(currentYear, currentMonth, day);
                  const status = getDateStatus(dateStr);
                  const isSelected = selectedDate === dateStr;

                  let cellClass =
                    'aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-all relative ';

                  if (isSelected) {
                    cellClass += 'bg-amber-400 text-stone-900 shadow-md shadow-amber-200 scale-105 ';
                  } else if (status === 'past') {
                    cellClass += 'text-stone-300 cursor-default ';
                  } else if (status === 'booked') {
                    cellClass +=
                      'bg-rose-50 text-rose-300 cursor-not-allowed line-through ';
                  } else if (status === 'limited') {
                    cellClass +=
                      'bg-amber-50 text-amber-600 cursor-pointer hover:bg-amber-100 border border-amber-200 ';
                  } else {
                    cellClass +=
                      'bg-emerald-50 text-emerald-700 cursor-pointer hover:bg-emerald-100 border border-emerald-200 ';
                  }

                  return (
                    <button
                      key={dateStr}
                      type="button"
                      onClick={() => handleDayClick(dateStr, status)}
                      className={cellClass}
                      title={
                        status === 'booked'
                          ? 'Fully booked'
                          : status === 'limited'
                          ? 'Limited availability'
                          : status === 'available'
                          ? 'Available'
                          : ''
                      }
                    >
                      {day}
                      {status === 'limited' && !isSelected && (
                        <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-amber-400"></span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-5 mt-4 px-1">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-emerald-50 border border-emerald-200"></div>
                <span className="text-xs text-stone-500">Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-amber-50 border border-amber-200"></div>
                <span className="text-xs text-stone-500">Limited Availability</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-rose-50 border border-rose-100"></div>
                <span className="text-xs text-stone-500">Fully Booked</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-white border border-stone-200"></div>
                <span className="text-xs text-stone-500">Past / Unavailable</span>
              </div>
            </div>
          </div>

          {/* Right — Status Panel */}
          <div className="lg:col-span-2 sticky top-24 space-y-4">
            {/* Selected Date Info */}
            <div className="bg-white rounded-2xl border border-stone-100 shadow-lg shadow-stone-100 overflow-hidden">
              <div className="bg-gradient-to-br from-stone-800 to-stone-900 px-6 py-5">
                <p className="text-amber-400 text-xs tracking-widest uppercase font-medium mb-1">
                  Selected Date
                </p>
                <p
                  className="text-2xl font-light text-white"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {selectedDate
                    ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-AU', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })
                    : 'No date selected'}
                </p>
              </div>

              <div className="px-6 py-5">
                {!selectedDate && (
                  <div className="flex items-start gap-3 text-stone-400">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                      <i className="ri-calendar-line text-base"></i>
                    </div>
                    <p className="text-sm leading-relaxed">
                      Click any highlighted date on the calendar to check its availability status.
                    </p>
                  </div>
                )}

                {selectedDate && selectedStatus === 'available' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-100">
                        <i className="ri-checkbox-circle-fill text-emerald-600 text-base"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-emerald-700">Fully Available</p>
                        <p className="text-xs text-stone-400">This date is open for bookings</p>
                      </div>
                    </div>
                    <p className="text-xs text-stone-500 leading-relaxed">
                      Great news! This date is available. Request a quote below and we'll confirm your booking within 24 hours.
                    </p>
                    <button
                      type="button"
                      onClick={scrollToQuote}
                      className="w-full py-3 bg-gradient-to-r from-amber-400 to-amber-500 text-stone-900 text-sm font-semibold rounded-lg hover:from-amber-300 hover:to-amber-400 transition-all cursor-pointer whitespace-nowrap shadow-md shadow-amber-200"
                    >
                      Request a Quote for This Date
                    </button>
                  </div>
                )}

                {selectedDate && selectedStatus === 'limited' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100">
                        <i className="ri-time-line text-amber-600 text-base"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-amber-700">Limited Availability</p>
                        <p className="text-xs text-stone-400">Only a few slots remaining</p>
                      </div>
                    </div>
                    <p className="text-xs text-stone-500 leading-relaxed">
                      This date has limited availability. We recommend submitting your quote request as soon as possible to secure your spot.
                    </p>
                    <button
                      type="button"
                      onClick={scrollToQuote}
                      className="w-full py-3 bg-gradient-to-r from-amber-400 to-amber-500 text-stone-900 text-sm font-semibold rounded-lg hover:from-amber-300 hover:to-amber-400 transition-all cursor-pointer whitespace-nowrap shadow-md shadow-amber-200"
                    >
                      Request a Quote — Act Fast
                    </button>
                  </div>
                )}

                {selectedDate && selectedStatus === 'booked' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-rose-100">
                        <i className="ri-close-circle-fill text-rose-500 text-base"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-rose-600">Fully Booked</p>
                        <p className="text-xs text-stone-400">No availability on this date</p>
                      </div>
                    </div>
                    <p className="text-xs text-stone-500 leading-relaxed">
                      Unfortunately this date is fully booked. Please select a different date from the calendar or contact us directly — we may be able to accommodate you.
                    </p>
                    <button
                      type="button"
                      onClick={scrollToQuote}
                      className="w-full py-3 bg-stone-100 text-stone-600 text-sm font-semibold rounded-lg hover:bg-stone-200 transition-all cursor-pointer whitespace-nowrap"
                    >
                      Enquire About Another Date
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-stone-50 rounded-2xl border border-stone-100 px-6 py-5 space-y-3">
              <p className="text-xs text-stone-500 uppercase tracking-widest font-semibold">This Month at a Glance</p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-white rounded-xl py-3 border border-stone-100">
                  <p className="text-lg font-semibold text-emerald-600">
                    {Array.from({ length: daysInMonth }, (_, i) => i + 1).filter((d) => {
                      const ds = toDateStr(currentYear, currentMonth, d);
                      return getDateStatus(ds) === 'available';
                    }).length}
                  </p>
                  <p className="text-xs text-stone-400 mt-0.5">Open</p>
                </div>
                <div className="bg-white rounded-xl py-3 border border-stone-100">
                  <p className="text-lg font-semibold text-amber-500">
                    {Array.from({ length: daysInMonth }, (_, i) => i + 1).filter((d) => {
                      const ds = toDateStr(currentYear, currentMonth, d);
                      return getDateStatus(ds) === 'limited';
                    }).length}
                  </p>
                  <p className="text-xs text-stone-400 mt-0.5">Limited</p>
                </div>
                <div className="bg-white rounded-xl py-3 border border-stone-100">
                  <p className="text-lg font-semibold text-rose-500">
                    {Array.from({ length: daysInMonth }, (_, i) => i + 1).filter((d) => {
                      const ds = toDateStr(currentYear, currentMonth, d);
                      return getDateStatus(ds) === 'booked';
                    }).length}
                  </p>
                  <p className="text-xs text-stone-400 mt-0.5">Booked</p>
                </div>
              </div>
              <p className="text-xs text-stone-400 leading-relaxed">
                Availability updates regularly. For urgent enquiries call us directly on{' '}
                <a href="tel:0411164144" className="text-amber-600 font-medium hover:underline cursor-pointer">
                  0411 164 144
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
